import {computed, ref, watchEffect, type Ref} from 'vue';
import {useRouter} from 'vue-router';
import {useQuasar} from 'quasar';
import {useI18n} from 'vue-i18n';
import {useInterventionStore} from '@/ts/store/intervention-store';
import {useUserStore} from '@/ts/store/user-store';
import {useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useCdcDppFallback, type CdcDppFallbackMode} from '@/ts/composables/stateful/use-cdc-dpp-fallback';
import {hasGrantedProgramCoverage} from '@/ts/composables/pure/cdc-dpp-access';
import {pagesPath} from '@/ts/router/pages-path';

/**
 * Which flavor of the ELIGIBLE dialog to render, derived from the per-enrollment capability flags
 * the backend exposes in `cdcDpp` (see interventions-dto.ts):
 * - `INSURANCE` — insurance funding can be offered: verification flow + per-type fallback.
 * - `REWARD` — the enrollment carries the participation reward: the promise copy + Continue/Exit.
 * - `NEUTRAL` — neither capability (e.g. insurance switched off past the cutoff): eligibility info
 *   only + Continue/Exit. Nothing to offer, nothing to verify.
 * A missing flag falls back to the reward regime (the safe default: never advertise the insurance
 * flow without the backend confirming it). The not-eligible dialog is variant-independent.
 */
export type CdcDppEligibleVariant = 'INSURANCE' | 'REWARD' | 'NEUTRAL';

/** Reactive state and handlers exposed by {@link useCdcDppEligibility}. */
export interface UseCdcDppEligibilityReturn {
  showEligibilityDialog: Ref<boolean>;
  showInsuranceDialog: Ref<boolean>;
  eligibilityResult: Ref<boolean | null>;
  eligibleVariant: Ref<CdcDppEligibleVariant>;
  fallbackMode: Ref<CdcDppFallbackMode | null>;
  grantingYear: Ref<boolean>;
  onEligibilityContinue(): void;
  onEligibilityExit(): Promise<void>;
  onEligibilityCheckInsurance(): void;
  onInsuranceFunded(): Promise<void>;
  onInsuranceSkipped(): void;
  onInsuranceBack(): void;
  onFallbackBack(): void;
  onFallbackGrantYear(): Promise<void>;
  onFallbackSubscriberContinue(): void;
  onFallbackUpgrade(): void;
  onFallbackDeclineToOffer(): void;
  onFallbackContinue(): void;
  onFallbackExit(): Promise<void>;
}

/**
 * Owns the CDC DPP eligibility dialog state for the T2 Diabetes intervention.
 *
 * The modal must be shown whenever the user's eligibility has been evaluated
 * (`cdcDpp.eligible` is a boolean) and the user has not yet resolved their
 * decision (`cdcDpp.eligibilityAck`): on the first page of the first session
 * (where the evaluation lands), and — for the INSURANCE variant, whose flow
 * can be left unresolved — on any session page they re-enter through.
 *
 * The acknowledgement lives server-side (per enrollment): it is recorded via
 * POST cdc-dpp/acknowledge when the user reaches a decision terminal and read
 * back with the intervention, so the dismissal is consistent across tabs and
 * devices. A withdraw + re-enroll creates a fresh enrollment server-side,
 * which re-prompts by construction — no client-side cleanup needed.
 */
export function useCdcDppEligibility(): UseCdcDppEligibilityReturn {
  const api = useApi();
  const router = useRouter();
  const $q = useQuasar();
  const {t} = useI18n();
  const logger = useLogger('useCdcDppEligibility');
  const intervention = useInterventionStore();
  const userStore = useUserStore();
  const {iid, sid, pid} = useRouterSectionParams();

  const showEligibilityDialog = ref(false);
  const showInsuranceDialog = ref(false);
  const eligibilityResult = ref<boolean | null>(null);
  const handled = ref(false);

  // Which eligible-dialog flavor applies to this enrollment. Missing flags degrade to the reward
  // regime (safe default): the insurance flow is never advertised without explicit confirmation.
  const eligibleVariant = computed<CdcDppEligibleVariant>(() => {
    const status = intervention.value?.cdcDpp;
    if (status?.insuranceFundable) {
      return 'INSURANCE';
    }
    return (status?.participationReward ?? true) ? 'REWARD' : 'NEUTRAL';
  });

  // Mark the eligibility decision as RESOLVED for this enrollment — only on reaching a terminal
  // (insurance funded, 1-year granted, subscriber continues, covered continues, a reward/neutral
  // "continue", or a not-eligible "continue"). NOT on merely opening the form/fallback: persisting
  // it on the first click meant a mid-flow reload left the user enrolled with the decision
  // unresolved and no re-prompt. Persisted server-side (best-effort: on failure the worst case is
  // a re-prompt on the next load); `handled` keeps the current view settled without a refetch.
  function acknowledge(): void {
    handled.value = true;
    // Optimistic mirror of the server write: `handled` dies with this composable instance (route
    // remounts), and a stale in-store copy would re-open the dialog — or a racing putSession
    // response could stamp the flag back to false before the fire-and-forget call commits.
    if (intervention.value?.cdcDpp) {
      intervention.value.cdcDpp.eligibilityAck = true;
    }
    api.me.acknowledgeCdcDppEligibility(iid.value).catch((error: unknown) => {
      logger.error('Failed to persist the CDC DPP eligibility ack: {}', error)();
    });
  }

  // Eligible (or not-eligible) user opted out: withdraw the enrollment and leave the session. Even
  // if the BE withdraw fails we must move the user out, otherwise they are stranded on a locked
  // page. No ack cleanup: re-enrolling creates a fresh enrollment server-side, which re-prompts.
  async function onEligibilityExit(): Promise<void> {
    handled.value = true;
    try {
      await api.me.withdrawIntervention(iid.value);
    } catch {
      // Swallow: even if the BE call fails we must close the dialog and
      // move the user out of the session, otherwise they are stranded.
    }
    showEligibilityDialog.value = false;
    void router.replace(pagesPath.intervention.interventionsList);
  }

  // The program-completion fallback (per user type) lives in its own composable; it delegates the
  // un-enroll branch back to onEligibilityExit, and acknowledges the decision (ack) when the user
  // reaches a non-exit terminal (grant / continue / upgrade).
  const fallback = useCdcDppFallback(iid, onEligibilityExit, acknowledge);
  const {fallbackMode, runFallback} = fallback;

  const isFirstSessionFirstPage = computed((): boolean => {
    const firstSessionId = intervention.value?.sessions[0]?.id;
    return firstSessionId === sid.value && pid.value === 1;
  });

  watchEffect(() => {
    if (handled.value) {
      return;
    }
    const status = intervention.value?.cdcDpp;
    // Transitional fallback: a pre-cdcDpp backend sends the flat legacy field instead. Falling
    // back keeps the gate rendering (reward variant via eligibleVariant's safe default) so the
    // dialogs — including the medically informative not-eligible one — never silently disappear
    // in a mixed-deploy window. Remove once every environment serves cdcDpp.
    const eligible = status ? status.eligible : intervention.value?.cdcDppEligible;
    if (typeof eligible !== 'boolean') {
      return;
    }
    // The gate is anchored to the first page of the first session — where the assessment that
    // produces the outcome completes (the dialog pops right there). But completing that assessment
    // marks page 1 as done, so a user who leaves with the decision unresolved re-enters the session
    // on a LATER page and the anchor alone would never re-prompt them. Re-prompting on any page is
    // deliberately limited to the INSURANCE variant: it is the only flow that can legitimately end
    // without a terminal (Upgrade navigates away unresolved by design, and the multi-step form can
    // be reloaded mid-flight). REWARD/NEUTRAL and the not-eligible dialog resolve in a single click
    // on the spot — and widening them would re-prompt every pre-insurance enrollment, whose real
    // decisions predate the server-side ack column and were never recorded.
    const pendingInsuranceDecision = eligible && eligibleVariant.value === 'INSURANCE';
    if (!isFirstSessionFirstPage.value && !pendingInsuranceDecision) {
      return;
    }
    if (status?.eligibilityAck) {
      return;
    }
    // An eligible user whose programme access is already a settled grant — insurance-funded, or a
    // gift covering the year — has no decision left here, so skip the gate entirely. This catches
    // enrollments settled without a recorded ack. Subscribers and voucher
    // holders are deliberately NOT covered (we still want to offer them the insurance route). Wait
    // for the profile to load before deciding so a not-yet-loaded contract doesn't flash the gate.
    if (eligible) {
      if (!userStore.userData) {
        return;
      }
      if (hasGrantedProgramCoverage(userStore.userData.lastContract)) {
        handled.value = true;
        return;
      }
    }

    handled.value = true;
    eligibilityResult.value = eligible;
    showEligibilityDialog.value = true;
  });

  // INSURANCE variant: an eligible user's "continue without insurance" → run the per-type fallback
  // (it acknowledges on its own terminal). In the REWARD/NEUTRAL variants "Continue" IS the
  // terminal (straight into the session, the reward — if any — is granted by the BE at session 8),
  // as is a NOT-eligible user's "continue" in every variant → acknowledge here.
  function onEligibilityContinue(): void {
    showEligibilityDialog.value = false;
    if (eligibilityResult.value === true && eligibleVariant.value === 'INSURANCE') {
      runFallback();
    } else {
      acknowledge();
    }
  }

  // Eligible user chose to check insurance: open the verification dialog. Opening is NOT a terminal,
  // so we don't acknowledge yet — a reload mid-form re-shows the root so they can re-decide.
  function onEligibilityCheckInsurance(): void {
    showEligibilityDialog.value = false;
    showInsuranceDialog.value = true;
  }

  // The user accepted insurance funding: the backend swapped their contract (insurance-backed
  // license granted, subscription cancelled if any). Acknowledge (terminal), confirm it visibly,
  // and refresh the profile so paywall/subscription state reflects the new contract.
  async function onInsuranceFunded(): Promise<void> {
    acknowledge();
    $q.notify({
      type: 'positive',
      message: t('insurance_funded_confirmation'),
    });
    try {
      userStore.setUserData(await api.me.getMe());
    } catch (error) {
      // Non-fatal: the server state is already correct; the next full load reflects it.
      logger.error('Failed to refresh user data after insurance funding: {}', error)();
    }
  }

  // The insurance dialog closed without funding (declined consent, unable to verify, or skipped):
  // the user is eligible but not insurance-funded, so run the same program-completion fallback.
  function onInsuranceSkipped(): void {
    showInsuranceDialog.value = false;
    runFallback();
  }

  // Return to the root decision modal from a sub-dialog — the insurance form's X, or a per-type
  // fallback prompt (upgrade / subscriber / voucher / covered) — so the user can re-choose (check
  // insurance / continue / exit). Closes whichever sub-dialog is open; not a skip, no withdraw.
  // The free-year OFFER does NOT use this (it stays terminal — paid options aren't re-exposed).
  function backToRoot(): void {
    // Inert while the free-year grant is in flight: bouncing to the root would re-expose the paid
    // options while the grant lands underneath (and its toast/ack would fire over that dialog).
    if (fallback.grantingYear.value) {
      return;
    }
    showInsuranceDialog.value = false;
    fallbackMode.value = null;
    showEligibilityDialog.value = true;
  }

  return {
    showEligibilityDialog,
    showInsuranceDialog,
    eligibilityResult,
    eligibleVariant,
    onEligibilityContinue,
    onEligibilityExit,
    onEligibilityCheckInsurance,
    onInsuranceFunded,
    onInsuranceSkipped,
    onInsuranceBack: backToRoot,
    onFallbackBack: backToRoot,
    fallbackMode,
    grantingYear: fallback.grantingYear,
    onFallbackGrantYear: fallback.onFallbackGrantYear,
    onFallbackSubscriberContinue: fallback.onFallbackSubscriberContinue,
    onFallbackUpgrade: fallback.onFallbackUpgrade,
    onFallbackDeclineToOffer: fallback.onFallbackDeclineToOffer,
    onFallbackContinue: fallback.onFallbackContinue,
    onFallbackExit: fallback.onFallbackExit,
  };
}
